import { useState, useEffect, useCallback, useRef } from 'react';
import { DeviceEventEmitter } from 'react-native';
import RevenueCatService from '@/services/revenueCatService';
import type { CustomerInfo } from 'react-native-purchases';
import { appIconService } from '@/services/appIconService';
import { supabase } from '@/lib/supabase';
import { billConfigCacheService } from '@/services/billConfigCacheService';
import { getServerPlusStatus, refreshServerPlusStatus, PlusStatusResponse } from '@/services/apiService';

/**
 * Hook to check and monitor entitlement status
 * 
 * @param entitlementId - The entitlement identifier to check (defaults to "Padrinho Plus")
 * @returns Object containing entitlement status, loading state, and refresh function
 * 
 * @example
 * ```tsx
 * const { hasEntitlement, isLoading, refresh } = useEntitlement('Padrinho Plus');
 * 
 * if (hasEntitlement) {
 *   return <PremiumFeature />;
 * }
 * ```
 */
export function useEntitlement(entitlementId: string = 'Padrinho Plus') {
  const [hasEntitlement, setHasEntitlement] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  const checkEntitlement = useCallback(async (forceRefresh: boolean = false) => {
    try {
      setIsLoading(true);
      setError(null);

      // Get customer info (force refresh invalidates cache first)
      const info = forceRefresh 
        ? await RevenueCatService.refreshCustomerInfo()
        : await RevenueCatService.getCustomerInfo();
      setCustomerInfo(info);

      if (!info) {
        setHasEntitlement(false);
        return;
      }

      // Check if entitlement is active
      const hasActive = info.entitlements.active[entitlementId] !== undefined;
      setHasEntitlement(hasActive);

      console.log(`🔒 [useEntitlement] ${entitlementId}:`, hasActive ? 'ACTIVE' : 'INACTIVE', forceRefresh ? '(forced refresh)' : '');
    } catch (err) {
      console.error('❌ [useEntitlement] Failed to check entitlement:', err);
      setError(err instanceof Error ? err.message : 'Failed to check entitlement');
      setHasEntitlement(false);
    } finally {
      setIsLoading(false);
    }
  }, [entitlementId]);

  // Check entitlement on mount and when entitlementId changes
  useEffect(() => {
    checkEntitlement();
  }, [checkEntitlement]);

  return {
    hasEntitlement,
    isLoading,
    customerInfo,
    error,
    refresh: () => checkEntitlement(false),
    forceRefresh: () => checkEntitlement(true),
  };
}

/**
 * Convenience hook specifically for "Padrinho Plus" entitlement
 * 
 * Listens for real-time subscription changes via RevenueCat listener
 * and automatically refreshes status when changes are detected
 * 
 * @example
 * ```tsx
 * const { hasPadrinhoPlus, isLoading } = usePadrinhoPlus();
 * 
 * if (isLoading) return <Loading />;
 * if (!hasPadrinhoPlus) return <UpgradePrompt />;
 * return <PremiumContent />;
 * ```
 */
export function usePadrinhoPlus() {
  const result = useEntitlement('Padrinho Plus');
  const hasUpdatedIcon = useRef(false);
  const previousStatus = useRef<boolean | null>(null);

  // Listen for real-time subscription changes from two distinct sources:
  //   1. `subscription:status-changed` — emitted by `syncPlusStatusToDatabase`
  //      after the server confirms a Plus status delta (purchase, restore,
  //      cancellation that the client already noticed).
  //   2. `revenuecat:customer-info-updated` — emitted by the RevenueCat SDK
  //      listener whenever customer info changes, INCLUDING the identity
  //      swap that happens when `Purchases.logIn(userId)` runs after auth.
  //      Without this branch, screens that mounted before identifyUser
  //      finished (or before INITIAL_SESSION-triggered identify on warm
  //      app open) stay stuck on the anonymous device user's entitlement
  //      and never light up Plus for an actually-paying user.
  useEffect(() => {
    console.log('👂 usePadrinhoPlus: Setting up listeners for subscription changes');

    const statusSubscription = DeviceEventEmitter.addListener(
      'subscription:status-changed',
      async (data: { hasPadrinhoPlus: boolean }) => {
        console.log('🔔 usePadrinhoPlus: subscription:status-changed', data);
        await result.forceRefresh();
      }
    );

    const sdkSubscription = DeviceEventEmitter.addListener(
      'revenuecat:customer-info-updated',
      async (data: { hasPadrinhoPlus: boolean }) => {
        console.log('🔔 usePadrinhoPlus: revenuecat:customer-info-updated', data);
        await result.forceRefresh();
      }
    );

    return () => {
      console.log('🔇 usePadrinhoPlus: Removing subscription listeners');
      statusSubscription.remove();
      sdkSubscription.remove();
    };
  }, [result.forceRefresh]);

  useEffect(() => {
    if (!result.isLoading && previousStatus.current !== result.hasEntitlement) {
      previousStatus.current = result.hasEntitlement;
      
      if (!hasUpdatedIcon.current || result.hasEntitlement !== previousStatus.current) {
        hasUpdatedIcon.current = true;
        appIconService.setIconForSubscription(result.hasEntitlement);
      }

      // Sync RevenueCat status to database
      syncPlusStatusToDatabase(result.hasEntitlement);
    }
  }, [result.hasEntitlement, result.isLoading]);

  return {
    ...result,
    hasPadrinhoPlus: result.hasEntitlement,
  };
}

/**
 * Sync RevenueCat Plus status to database is_plus_user field
 * Uses server endpoint with admin privileges to bypass RLS
 */
async function syncPlusStatusToDatabase(hasPadrinhoPlus: boolean) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.warn('⚠️ No session found, cannot sync Plus status');
      return;
    }

    console.log('💎 Syncing Plus status to database via server:', hasPadrinhoPlus);
    
    let syncedViaServer = false;
    let serverConfirmedStatus = hasPadrinhoPlus;
    let messageUsage = { remainingMessages: 25, limitReached: false };
    
    // Try server endpoint first (bypasses RLS and returns message usage)
    try {
      const response = await fetch('https://mobile.padrinho.app/api/user/sync-plus-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ hasPadrinhoPlus })
      });

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const result = await response.json();
        if (response.ok && result.success) {
          console.log('✅ Synced Plus status via server:', {
            isPlusUser: result.is_plus_user,
            remaining: result.remainingMessages,
            limitReached: result.limitReached
          });
          syncedViaServer = true;
          serverConfirmedStatus = result.is_plus_user;
          messageUsage = {
            remainingMessages: result.remainingMessages,
            limitReached: result.limitReached
          };
        } else {
          console.warn('⚠️ Server sync failed, trying direct update:', result.error || result);
        }
      } else {
        console.warn('⚠️ Server returned non-JSON, endpoint may not be deployed yet');
      }
    } catch (fetchError) {
      console.warn('⚠️ Server sync endpoint unavailable, trying direct update:', fetchError);
    }

    // Note: No fallback needed - server sync handles all profile updates
    if (!syncedViaServer) {
      console.warn('⚠️ Server sync failed - Plus status may not be persisted until next sync');
    }

    // Clear Bill config cache
    console.log('🔄 Clearing Bill config cache after subscription change');
    await billConfigCacheService.invalidateAllConfigCaches();
    
    // Emit unified event with server-confirmed status AND message usage
    // BillContext uses this to update both Plus status and limit state
    console.log('📢 Emitting subscription:status-changed event:', {
      hasPadrinhoPlus: serverConfirmedStatus,
      ...messageUsage
    });
    DeviceEventEmitter.emit('subscription:status-changed', { 
      hasPadrinhoPlus: serverConfirmedStatus,
      remainingMessages: messageUsage.remainingMessages,
      limitReached: messageUsage.limitReached
    });
  } catch (err) {
    console.error('❌ Error syncing Plus status:', err);
  }
}

/**
 * Server-verified Padrinho Plus status hook
 * 
 * SECURITY: Uses RevenueCat REST API on server to verify subscription status
 * This is the authoritative source of truth - never trust client SDK alone for gating
 * 
 * @example
 * ```tsx
 * const { hasPadrinhoPlus, isLoading, remainingMessages } = useServerVerifiedPlus();
 * 
 * if (isLoading) return <Loading />;
 * if (!hasPadrinhoPlus) return <UpgradePrompt />;
 * return <PremiumContent />;
 * ```
 */
export function useServerVerifiedPlus() {
  const [status, setStatus] = useState<PlusStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async (forceRefresh: boolean = false) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = forceRefresh 
        ? await refreshServerPlusStatus()
        : await getServerPlusStatus();

      setStatus(result);

      if (!result.success && result.error) {
        setError(result.error);
      }

      console.log('💎 [useServerVerifiedPlus] Status:', {
        hasPadrinhoPlus: result.hasPadrinhoPlus,
        source: result.source,
        forceRefresh
      });
    } catch (err) {
      console.error('❌ [useServerVerifiedPlus] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to verify Plus status');
      setStatus(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Listen for subscription changes to refresh status
  useEffect(() => {
    const subscription = DeviceEventEmitter.addListener(
      'subscription:status-changed',
      () => fetchStatus(true)
    );

    return () => subscription.remove();
  }, [fetchStatus]);

  return {
    hasPadrinhoPlus: status?.hasPadrinhoPlus ?? false,
    isLoading,
    error,
    messageCount: status?.messageCount ?? 0,
    remainingMessages: status?.remainingMessages ?? 30,
    monthlyLimit: status?.monthlyLimit ?? 30,
    limitReached: status?.limitReached ?? false,
    expiresAt: status?.expiresAt ?? null,
    source: status?.source ?? 'revenueCat_fallback_false',
    refresh: () => fetchStatus(false),
    forceRefresh: () => fetchStatus(true),
  };
}
